import { config } from 'config';
import { safeJsonParse } from '../utils/securityUtils';
import api, { rawFetchWithAuth } from './apiClient';

const API_URL = config.apiUrl;

export interface UserVerificationCompany {
  id: string;
  name: string;
  role: string;
  isActive: boolean;
}

export interface VerificationDetail {
  id: string; // Template verification ID
  _id?: string; // Alternative field for UserCompanyVerification ID
  userVerificationId?: string; // UserCompanyVerification ID (for submitted verifications)
  userCompanyVerificationId?: string; // Another possible field name
  name: string;
  type: string;
  category?: string;
  description?: string;
  isRequired: boolean;
  status: 'not_submitted' | 'pending' | 'in_review' | 'approved' | 'rejected' | 'expired';
  documentUrl?: string;
  documentoId?: string; // GridFS document ID
  certificateNumber?: string;
  submittedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  expiryDate?: string;
  validityPeriod?: number;
}

export interface UserCompanyVerifications {
  company: UserVerificationCompany;
  verifications: {
    total: number;
    required: number;
    completed: number;
    pending: number;
    expired: number;
    details: VerificationDetail[];
  };
  complianceStatus: 'compliant' | 'partial' | 'non_compliant';
}

export interface SubmitVerificationData {
  documentoId?: string;
  documentUrl?: string;
  certificateNumber?: string;
  expiryDate?: string;
  notes?: string;
}

class UserVerificationsApi {
  // Get all verifications for the current user across all companies
  async getMyVerifications(): Promise<UserCompanyVerifications[]> {
    try {
      const user = safeJsonParse<{ id?: string; _id?: string }>(localStorage.getItem('user'), {});
      const userId = user.id || user._id;
      if (!userId) throw new Error('User ID not found');

      const result = await api.get(`/verifications/users/${userId}/all`);

      if (result.success && result.data) {
        return result.data;
      }
      return result;
    } catch (error: unknown) {
      throw error;
    }
  }

  // Get verifications for a specific user (requires permissions)
  async getUserVerifications(userId: string): Promise<UserCompanyVerifications[]> {
    try {
      const result = await api.get(`/verifications/users/${userId}/all`);

      // Handle response format
      if (result.success && result.data) {
        return result.data;
      }
      return result;
    } catch (error: unknown) {
      throw error;
    }
  }

  // Get user verifications for a specific company
  async getUserCompanyVerifications(userId: string, companyId: string): Promise<UserCompanyVerifications> {
    try {
      return await api.get(`/verifications/users/${userId}/companies/${companyId}`);
    } catch (error: unknown) {
      throw error;
    }
  }

  // Submit or update a verification
  async submitVerification(
    companyId: string,
    verificationId: string,
    data: SubmitVerificationData
  ): Promise<any> {
    try {
      return await api.post(`/verifications/companies/${companyId}/verifications/${verificationId}/submit`, data);
    } catch (error: unknown) {
      throw error;
    }
  }

  // Upload document for verification (FormData requires raw fetch)
  async uploadVerificationDocument(file: File): Promise<{ documentoId: string; url?: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await rawFetchWithAuth(`${API_URL}/upload/verification-document`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(errorData.message || 'Upload failed');
      }

      return response.json();
    } catch (error: unknown) {
      throw error;
    }
  }

  // Review a user verification (approve or reject)
  async reviewVerification(
    userVerificationId: string,
    decision: 'approve' | 'reject',
    rejectionReason?: string,
    expiryDate?: string
  ): Promise<any> {
    try {
      const data: any = {
        status: decision === 'approve' ? 'approved' : 'rejected'
      };

      if (decision === 'approve' && expiryDate) {
        data.expiryDate = expiryDate;
      }

      if (decision === 'reject' && rejectionReason) {
        data.rejectionReason = rejectionReason;
      }

      return await api.put(`/verifications/user-verifications/${userVerificationId}/review`, data);
    } catch (error: unknown) {
      throw error;
    }
  }
}

export const userVerificationsApi = new UserVerificationsApi();
